import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as z from 'zod/v4-mini';
import { useSystemFetch } from '~/composables/use-api';
import { usePromiseDedupe } from '~/utils/async';

const OnboardingInfoSchema = z.object({
	requireCreateUser: z.boolean(),
	requireAcceptTerms: z.boolean(),
});

const SystemInfoResult = z.object({
	onboarding: z.optional(OnboardingInfoSchema),
});

type OnboardingInfo = z.infer<typeof OnboardingInfoSchema>;

export interface AcceptTermsPayload {
	fullName: string;
	acceptTerms: boolean;
	acceptMarketing: boolean;
}

export const useSystemInfo = defineStore('system-info', () => {
	const fetch = useSystemFetch();

	/**
	 * Onboarding requirements returned by `/system/info`. `null` means either
	 * the probe hasn't run yet, it failed, or the server reported no
	 * requirements — all three collapse to "no onboarding required".
	 */
	const onboarding = ref<OnboardingInfo | null>(null);
	const isInitialized = ref(false);
	const acceptTermsLoading = ref(false);
	const acceptTermsError = ref<unknown | null>(null);

	const refreshPromise = usePromiseDedupe();

	async function refresh() {
		if (refreshPromise.pending) {
			await refreshPromise.wait();
			return;
		}

		refreshPromise.create();

		try {
			await runRefresh();
		}
		finally {
			isInitialized.value = true;
			refreshPromise.resolve();
		}
	}

	async function runRefresh() {
		try {
			const result = await fetch('system/info');
			onboarding.value = SystemInfoResult.parse(result).onboarding ?? null;
		}
		catch (error) {
			console.warn('[useSystemInfo] /system/info probe failed; keeping last known state', error);
		}
	}

	async function ensureLoaded() {
		if (isInitialized.value) {
			return;
		}

		await refresh();
	}

	async function acceptTerms(payload: AcceptTermsPayload): Promise<boolean> {
		acceptTermsError.value = null;
		acceptTermsLoading.value = true;

		try {
			await fetch('system/bootstrap', { method: 'POST', body: payload });
			await refresh();
			return true;
		}
		catch (error) {
			acceptTermsError.value = error;
			return false;
		}
		finally {
			acceptTermsLoading.value = false;
		}
	}

	function clearAcceptTermsError() {
		acceptTermsError.value = null;
	}

	return {
		acceptTermsLoading: computed(() => acceptTermsLoading.value),
		acceptTermsError: computed(() => acceptTermsError.value),
		refresh,
		ensureLoaded,
		acceptTerms,
		clearAcceptTermsError,
		requireCreateUser: computed(() => onboarding.value?.requireCreateUser === true),
		requireAcceptTerms: computed(() => onboarding.value?.requireAcceptTerms === true),
	};
});
